import { useState, useCallback, useEffect } from 'react';
import {
  addMinutes,
  startOfMonth,
  endOfMonth,
  startOfDay,
  endOfDay,
  format,
  parseISO,
  formatISO,
  startOfWeek,
  endOfWeek,
} from 'date-fns';
import { useHistory, useLocation } from 'react-router-dom';

import { getAllPolygraphies } from 'api/polygraphies';
import { getAllPolysomnographies } from 'api/polysomnographies';

import { nameDecorator } from 'helpers/name';
import { filtersToQueryString } from 'helpers/filtering';
import { parseDatetime } from 'helpers/timezone';
import { EVENTS_WITH_CANCELLED_STATUSES } from 'config/polygraphy';

import EventCalendar from 'components/calendar/calendar';
import PolygraphyModal from 'components/calendar/modals/polygraphy';
import PolysomnographyModal from 'components/calendar/modals/polysomnography';

const TechnicianExamsCalendar = () => {
  const history = useHistory();
  const location = useLocation();
  const [view, setView] = useState('day');
  const [date, setDate] = useState(
    formatISO(new Date(), { representation: 'date' }),
  );
  const [fetching, setFetching] = useState(true);
  const [showEventModal, setShowEventModal] = useState(false);
  const [currentEvent, setCurrentEvent] = useState({});
  const [events, setEvents] = useState([]);

  const fetchData = useCallback(async (date, view) => {
    let filterString;

    switch (view) {
      case 'day':
        filterString = filtersToQueryString({
          from: [format(startOfDay(parseISO(date)), 'yyyy-MM-dd')],
          to: [format(endOfDay(parseISO(date)), 'yyyy-MM-dd')],
        });
        break;
      case 'week':
        filterString = filtersToQueryString({
          from: [format(startOfWeek(parseISO(date)), 'yyyy-MM-dd')],
          to: [format(endOfWeek(parseISO(date)), 'yyyy-MM-dd')],
        });
        break;
      case 'month':
        filterString = filtersToQueryString({
          from: [format(startOfMonth(parseISO(date)), 'yyyy-MM-dd')],
          to: [format(endOfMonth(parseISO(date)), 'yyyy-MM-dd')],
        });
        break;
      default:
        filterString = filtersToQueryString({
          from: [format(startOfDay(parseISO(date)), 'yyyy-MM-dd')],
          to: [format(endOfDay(parseISO(date)), 'yyyy-MM-dd')],
        });
        break;
    }

    const pgData = await getAllPolygraphies(1, filterString, 0);
    const psgData = await getAllPolysomnographies(1, filterString, 0);

    const pgEvents = pgData.items.map((p) => ({
      id: p.id,
      entity: 'pg',
      title: `${nameDecorator(
        p.customer?.firstName,
        p.customer?.lastName,
      )}: polygraphy`,
      start: parseDatetime(p.deliveryDate),
      end: addMinutes(parseDatetime(p.deliveryDate), 30),
      deliveryDate: parseDatetime(p.deliveryDate),
      pickupDate: parseDatetime(p.pickupDate),
      paymentBadge: p.paymentBadge,
      status: p.status,
      recordingStatus: p.recordingStatus,
      cancelled: EVENTS_WITH_CANCELLED_STATUSES.includes(p.status),
      technician: p.technician,
      customer: p.customer,
      notes: p.notes,
      backgroundColor: '#DCFCE7',
    }));

    const psgEvents = psgData.items.map((p) => ({
      id: p.id,
      entity: 'psg',
      title: `${nameDecorator(
        p.customer?.firstName,
        p.customer?.lastName,
      )}: polysomnography`,
      start: parseDatetime(p.deliveryDate),
      end: addMinutes(parseDatetime(p.deliveryDate), 60),
      deliveryDate: parseDatetime(p.deliveryDate),
      pickupDate: parseDatetime(p.pickupDate),
      paymentBadge: p.paymentBadge,
      status: p.status,
      recordingStatus: p.recordingStatus,
      technician: p.technician,
      customer: p.customer,
      notes: p.notes,
      backgroundColor: '#FEF3C7',
    }));

    setEvents([...pgEvents, ...psgEvents]);
    setFetching(false);
  }, []);

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const view = search.get('view') || 'day';
    const date =
      search.get('date') || formatISO(new Date(), { representation: 'date' });
    setView(view);
    setDate(date);

    fetchData(date, view);
  }, [location, fetchData]);

  const handleNavigate = (date, view) => {
    const formattedDate = formatISO(date, {
      representation: 'date',
    });

    history.push({
      pathname: location.pathname,
      search: `?view=${view}&date=${formattedDate}`,
    });
  };

  const handleSelectEvent = (event) => {
    setCurrentEvent(event);
    setShowEventModal(true);
  };

  const handleViewChange = (view) => {
    const formattedDate = formatISO(new Date(), {
      representation: 'date',
    });

    history.push({
      pathname: location.pathname,
      search: `?view=${view}&date=${formattedDate}`,
    });
  };

  const handleShowMore = (_, date) => {
    const formattedDate = formatISO(date, {
      representation: 'date',
    });

    history.push({
      pathname: location.pathname,
      search: `?view=day&date=${formattedDate}`,
    });
  };

  return (
    <>
      <main className="th-main">
        <section className="th-canvas">
          <header className="th-canvas-header post-header">
            <h2 className="th-canvas-title" data-test-screen-title>
              Calendar
            </h2>
          </header>
          <EventCalendar
            date={date}
            events={events}
            view={view}
            parentEntity="pg"
            onSelectEvent={handleSelectEvent}
            onNavigate={handleNavigate}
            onView={handleViewChange}
            onShowMore={handleShowMore}
            loading={fetching}
          />
        </section>
      </main>
      {showEventModal && currentEvent.entity === 'pg' && (
        <PolygraphyModal
          event={currentEvent}
          onClose={() => setShowEventModal(false)}
          onClickOutside={() => setShowEventModal(false)}
        />
      )}
      {showEventModal && currentEvent.entity === 'psg' && (
        <PolysomnographyModal
          event={currentEvent}
          onClose={() => setShowEventModal(false)}
          onClickOutside={() => setShowEventModal(false)}
        />
      )}
    </>
  );
};

export default TechnicianExamsCalendar;
